/**
 * Radio System - Team-based coordination and assist requests
 *
 * Architecture:
 * - TeamRadioNetwork: Global singleton managing all team channels
 * - RadioSystem: Per-unit system for sending/receiving requests
 * - Utility-based AI: Soldiers evaluate and respond to requests based on score
 *
 * How It Works:
 * 1. Soldier retreating from combat calls requestAssist() with enemy info
 * 2. Request broadcast to team channel with TTL (6 seconds)
 * 3. All teammates evaluate request via utility scoring (distance, health, ammo, threat)
 * 4. Best-scoring available soldier accepts (score > 0.55 threshold)
 * 5. Responding soldier transitions to 'assisting' state
 * 6. Moves to position, searches for enemy, engages or cancels
 *
 * Current Implementation:
 * - Assist requests during retreat (combat support)
 * - Utility scoring with busy penalties
 * - State machine integration (assisting state)
 * - Automatic expiration and cleanup
 *
 * Expansion Ideas:
 *
 * 1. Base Defense Requests
 *    Base under attack broadcasts:
 *    { type: 'defend_base', baseId, attackers: [...], threatLevel }
 *
 * 2. Resource Sharing
 *    Soldier finds multiple resources:
 *    { type: 'resource_sharing', resourceIds: [...], position }
 *
 * 3. Coordinated Attacks
 *    High-level soldier calls for attack:
 *    { type: 'attack_target', targetId, rallyPoint, minResponders: 3 }
 *
 * 4. Reconnaissance
 *    Scout reports enemy positions:
 *    { type: 'intel', enemies: [{id, level, position}], area }
 *
 * 5. Corpse Looting Calls
 *    High-value corpse available:
 *    { type: 'corpse_loot', corpseLevel, position, competitors }
 *
 * 6. Acknowledgment System
 *    Responders confirm receipt:
 *    - Prevents over-response (multiple soldiers to same request)
 *    - Requester knows help is coming
 *
 * 7. Priority Levels
 *    CRITICAL > HIGH > NORMAL > LOW
 *    Filter requests by priority, respond to highest first
 *
 * 8. Request Chains
 *    Initial responder can re-broadcast if overwhelmed:
 *    { type: 'reinforcement', originalRequest, currentSituation }
 *
 * Example Expansion - Base Defense:
 * ```javascript
 * // In TeamBase when under attack:
 * if (this.health < this.maxHealth * 0.5) {
 *   teamRadioNetwork.broadcastAssistRequest(this.team.id, {
 *     type: 'defend_base',
 *     sourceId: this.id,
 *     position: { x: this.x, y: this.y },
 *     threatLevel: 1 - (this.health / this.maxHealth),
 *     attackers: this.nearbyEnemies.map(e => e.id),
 *     reason: 'base_under_attack'
 *   });
 * }
 * ```
 *
 * @module RadioSystem
 */

import { clamp } from '../../utils/math.js';
import { debugLog } from '../../utils/debug.js';
import { THRESHOLD_CONFIG, RADIO_CONFIG } from '../../config.js';

// Default configuration constants
const DEFAULT_REQUEST_TTL = 6000;           // 6 seconds before request expires
const MIN_BROADCAST_INTERVAL = 2000;        // 2 seconds between broadcasts (prevent spam)
const DEFAULT_ARRIVAL_RADIUS = 48;          // Distance to destination considered "arrived"
const DEFAULT_ENGAGE_RADIUS = 160;          // Search radius for enemies at assist location

/**
 * TeamRadioNetwork - Global communication hub for all teams
 *
 * Manages separate radio channels for each team. Requests broadcast to team channel
 * are received by all teammates. Expired requests are automatically pruned.
 *
 * Singleton Pattern: One network instance shared across all units.
 */
class TeamRadioNetwork {
  constructor() {
    this.channels = new Map();  // teamId -> array of requests
    this.nextRequestId = 1;
  }

  /**
   * Broadcast an assist request to a team's radio channel
   *
   * Creates a request with unique ID, timestamp, and expiration.
   * All teammates will evaluate this request on their next update.
   *
   * @param {number} teamId - Team ID to broadcast to
   * @param {Object} payload - Request details
   * @param {number} payload.sourceId - Unit ID making the request
   * @param {number} payload.sourceLevel - Level of requesting unit
   * @param {Object} payload.position - {x, y} location of request
   * @param {number} payload.threatLevel - Threat level 0-1 (0=low, 1=critical)
   * @param {string} payload.reason - Reason for request ('combat_support', etc.)
   * @param {number} payload.enemyId - Optional: specific enemy ID
   * @param {number} payload.enemyLevel - Optional: enemy level
   * @param {number} payload.arrivalRadius - How close to get before "arrived"
   * @param {number} payload.engageRadius - Search radius for enemies
   * @param {number} payload.ttl - Time to live in milliseconds
   * @returns {Object} The created request object
   */
  broadcastAssistRequest(teamId, payload = {}) {
    if (!teamId) return null;

    const channel = this.#ensureChannel(teamId);
    const now = Date.now();
    const ttl = payload.ttl ?? DEFAULT_REQUEST_TTL;

    const request = {
      id: this.nextRequestId++,
      teamId,
      type: 'assist',
      sourceId: payload.sourceId,
      sourceLevel: payload.sourceLevel ?? 1,
      position: {
        x: payload.position?.x ?? 0,
        y: payload.position?.y ?? 0
      },
      threatLevel: clamp(payload.threatLevel ?? 0.5, 0, 1),
      reason: payload.reason ?? 'assist_request',
      enemyId: payload.enemyId ?? null,
      enemyLevel: payload.enemyLevel ?? null,
      arrivalRadius: payload.arrivalRadius ?? DEFAULT_ARRIVAL_RADIUS,
      engageRadius: payload.engageRadius ?? DEFAULT_ENGAGE_RADIUS,
      timestamp: now,
      expiresAt: now + ttl,
      active: true
    };

    channel.push(request);
    return { ...request };
  }

  /**
   * Broadcast a base defense request to a team's radio channel
   *
   * Creates a defense request for a base under attack.
   * Higher threat levels and more attackers increase response likelihood.
   *
   * @param {number} teamId - Team ID to broadcast to
   * @param {Object} payload - Request details
   * @param {number} payload.baseId - Base ID making the request
   * @param {Object} payload.position - {x, y} location of base
   * @param {number} payload.threatLevel - Threat level 0-1 based on health/attackers
   * @param {number[]} payload.attackerIds - Array of attacker unit IDs
   * @param {number} payload.attackerCount - Number of attackers
   * @param {number} payload.arrivalRadius - How close to get before "arrived"
   * @param {number} payload.patrolRadius - Patrol radius when defending
   * @param {number} payload.ttl - Time to live in milliseconds
   * @returns {Object} The created request object
   */
  broadcastDefenseRequest(teamId, payload = {}) {
    if (!teamId || !RADIO_CONFIG.BASE_DEFENSE_ENABLED) return null;

    const channel = this.#ensureChannel(teamId);
    const now = Date.now();
    const ttl = payload.ttl ?? RADIO_CONFIG.BASE_DEFENSE_REQUEST_TTL;

    const request = {
      id: this.nextRequestId++,
      teamId,
      type: 'defend_base',
      baseId: payload.baseId,
      position: {
        x: payload.position?.x ?? 0,
        y: payload.position?.y ?? 0
      },
      threatLevel: clamp(payload.threatLevel ?? 0.7, 0, 1),
      attackerIds: payload.attackerIds ?? [],
      attackerCount: payload.attackerCount ?? 0,
      arrivalRadius: payload.arrivalRadius ?? RADIO_CONFIG.BASE_DEFENSE_ARRIVAL_RADIUS,
      patrolRadius: payload.patrolRadius ?? RADIO_CONFIG.BASE_DEFENSE_PATROL_RADIUS,
      timestamp: now,
      expiresAt: now + ttl,
      active: true,
      responderCount: 0,  // Track how many soldiers are responding
      responderIds: []    // Track which soldiers are responding
    };

    channel.push(request);
    return { ...request };
  }

  /**
   * Get all active (non-expired) requests for a team
   *
   * Filters out expired requests and returns copies to prevent mutation.
   * Also performs cleanup by removing expired requests from the channel.
   *
   * @param {number} teamId - Team ID to get requests for
   * @param {number} now - Current timestamp (for testing/consistency)
   * @returns {Object[]} Array of active request objects
   */
  getActiveRequests(teamId, now = Date.now()) {
    const channel = this.channels.get(teamId);
    if (!channel || channel.length === 0) {
      return [];
    }

    const active = [];
    const remaining = [];

    for (const request of channel) {
      if (!request.active) continue;
      if (request.expiresAt > now) {
        active.push({ ...request });  // Return copy to prevent external mutation
        remaining.push(request);
      }
    }

    // Update channel with only non-expired requests (cleanup)
    this.channels.set(teamId, remaining);
    return active;
  }

  /**
   * Cancel a specific request by ID
   *
   * Marks request as inactive and immediately expired.
   * Used when requester resolves their own situation.
   *
   * @param {number} teamId - Team ID of the request
   * @param {number} requestId - Unique request ID to cancel
   */
  cancelRequest(teamId, requestId) {
    const channel = this.channels.get(teamId);
    if (!channel) return;

    const now = Date.now();
    for (const request of channel) {
      if (request.id === requestId) {
        request.active = false;
        request.expiresAt = now;
        break;
      }
    }
  }

  /**
   * Acknowledge a defense request by adding a responder
   *
   * Tracks which soldiers are responding to prevent over-response.
   * Defense requests have a maximum responder count.
   *
   * @param {number} teamId - Team ID of the request
   * @param {number} requestId - Unique request ID
   * @param {number} responderId - Unit ID of responder
   * @returns {boolean} True if acknowledgment succeeded
   */
  acknowledgeDefenseRequest(teamId, requestId, responderId) {
    const channel = this.channels.get(teamId);
    if (!channel) return false;

    for (const request of channel) {
      if (request.id === requestId && request.type === 'defend_base') {
        // Check if already at max responders
        if (request.responderCount >= RADIO_CONFIG.BASE_DEFENSE_MAX_RESPONDERS) {
          return false;
        }
        // Check if this unit is already responding
        if (request.responderIds.includes(responderId)) {
          return false;
        }
        // Add responder
        request.responderCount++;
        request.responderIds.push(responderId);
        return true;
      }
    }
    return false;
  }

  /**
   * Remove a responder from a defense request
   *
   * Called when a defending soldier dies or cancels defense.
   *
   * @param {number} teamId - Team ID of the request
   * @param {number} requestId - Unique request ID
   * @param {number} responderId - Unit ID of responder to remove
   */
  removeDefenseResponder(teamId, requestId, responderId) {
    const channel = this.channels.get(teamId);
    if (!channel) return;

    for (const request of channel) {
      if (request.id === requestId && request.type === 'defend_base') {
        const index = request.responderIds.indexOf(responderId);
        if (index !== -1) {
          request.responderIds.splice(index, 1);
          request.responderCount = Math.max(0, request.responderCount - 1);
        }
        break;
      }
    }
  }

  /**
   * Prune expired requests from team channel(s)
   *
   * If teamId is undefined, prunes all teams (global cleanup).
   * Otherwise prunes specific team's channel.
   *
   * @param {number} teamId - Team ID to prune (undefined = all teams)
   * @param {number} now - Current timestamp
   */
  pruneExpired(teamId, now = Date.now()) {
    if (typeof teamId === 'undefined') {
      // Prune all team channels
      for (const [id] of this.channels) {
        this.pruneExpired(id, now);
      }
      return;
    }

    const channel = this.channels.get(teamId);
    if (!channel || channel.length === 0) {
      return;
    }

    const filtered = channel.filter((request) => request.active && request.expiresAt > now);
    this.channels.set(teamId, filtered);
  }

  /**
   * Ensure a team has a channel (create if doesn't exist)
   * @private
   */
  #ensureChannel(teamId) {
    if (!this.channels.has(teamId)) {
      this.channels.set(teamId, []);
    }
    return this.channels.get(teamId);
  }
}

/**
 * Global singleton instance of TeamRadioNetwork
 * All units share this network for team communication
 */
export const teamRadioNetwork = new TeamRadioNetwork();

/**
 * RadioSystem - Per-unit radio for sending/receiving assist requests
 *
 * Each soldier has their own RadioSystem instance registered as a unit system.
 * Updated every frame via unit.updateSystems() pipeline.
 *
 * Lifecycle:
 * 1. Constructor - Set options (thresholds, ranges, TTL)
 * 2. initialize(unit) - Link to unit
 * 3. setAI(ai) - Link to AI (for assist objective handling)
 * 4. update(delta, context) - Every frame:
 *    - Prune expired requests
 *    - Check if already responding
 *    - Evaluate active requests
 *    - Accept best scoring request if above threshold
 *
 * Sending Requests:
 * - requestAssist(payload) - Broadcast to team
 * - resolveOutgoingRequest() - Cancel when resolved
 *
 * Receiving Requests:
 * - Automatic evaluation in update()
 * - Notifies AI via handleAssistObjectiveUpdate()
 * - AI decides to accept via shouldRespondToAssist()
 * - Sends ALLY_ASSIST_REQUEST event to transition to 'assisting' state
 *
 * Configuration:
 * - utilityThreshold: 0.55 (score must exceed to respond)
 * - maxAssistDistance: 600px (max range to consider requests)
 * - requestTtl: 6000ms (how long requests last)
 * - minBroadcastInterval: 2000ms (prevent spam)
 */
export class RadioSystem {
  /**
   * Create a new RadioSystem for a unit
   * @param {Object} options - Configuration options
   * @param {number} options.utilityThreshold - Min score to accept request (default 0.55)
   * @param {number} options.maxAssistDistance - Max distance to respond (default 600)
   * @param {number} options.requestTtl - Request lifetime in ms (default 6000)
   * @param {number} options.minBroadcastInterval - Min time between broadcasts (default 2000)
   */
  constructor(options = {}) {
    this.unit = null;
    this.ai = null;
    this.network = teamRadioNetwork;
    this.responding = false;          // Currently responding to a request
    this.currentObjective = null;     // Active assist objective
    this.activeRequest = null;        // Outgoing request we broadcast
    this.lastBroadcastTime = 0;       // Last time we broadcast (for cooldown)

    // Configuration
    this.utilityThreshold = options.utilityThreshold ?? 0.55;
    this.maxAssistDistance = options.maxAssistDistance ?? 600;
    this.requestTtl = options.requestTtl ?? DEFAULT_REQUEST_TTL;
    this.minBroadcastInterval = options.minBroadcastInterval ?? MIN_BROADCAST_INTERVAL;
  }

  /**
   * Initialize radio system with unit reference
   * Called by Unit.registerSystem()
   * @param {Unit} unit - The unit this radio belongs to
   */
  initialize(unit) {
    this.unit = unit;
  }

  /**
   * Link to unit's AI controller
   * Called by Soldier constructor after AI creation
   * @param {SoldierAI} ai - The AI controller
   */
  setAI(ai) {
    this.ai = ai;
  }

  /**
   * Main update loop - evaluate and respond to assist requests
   *
   * Called every frame via unit.updateSystems() pipeline.
   *
   * Flow:
   * 1. Prune expired requests from team channel
   * 2. If already responding, maintain current objective
   * 3. Get active requests from team channel
   * 4. Evaluate each request with utility scoring
   * 5. Accept best request if score > threshold
   * 6. Notify AI to transition to 'assisting' state
   *
   * @param {number} delta - Time delta in milliseconds
   * @param {Object} context - Game context (units, projectiles, etc.)
   */
  update(delta, context) {
    if (!this.unit || !this.unit.alive || !this.unit.team) return;

    const now = Date.now();
    this.network.pruneExpired(this.unit.team.id, now);

    if (this.ai && typeof this.ai.hasActiveAssist === 'function' && this.ai.hasActiveAssist()) {
      // AI is already working on an assist objective; keep current assignment
      this.responding = true;
      return;
    }

    if (this.responding && this.currentObjective) {
      // Keep current objective until AI clears it
      if (this.currentObjective.expiresAt && now > this.currentObjective.expiresAt) {
        this.completeAssistObjective('expired', { notifyAI: true });
      }
      return;
    }

    const requests = this.network.getActiveRequests(this.unit.team.id, now);
    if (!requests || requests.length === 0) {
      return;
    }

    let bestRequest = null;
    let bestScore = 0;
    let requestType = null;

    for (const request of requests) {
      // Skip self-requests for assist type
      if (request.type === 'assist' && request.sourceId === this.unit.id) continue;

      // Skip defense requests that are full
      if (request.type === 'defend_base' &&
          request.responderCount >= RADIO_CONFIG.BASE_DEFENSE_MAX_RESPONDERS) {
        continue;
      }

      const score = this.#evaluateRequest(request);
      const threshold = request.type === 'defend_base'
        ? RADIO_CONFIG.BASE_DEFENSE_UTILITY_THRESHOLD
        : this.utilityThreshold;

      if (score > bestScore && score >= threshold) {
        bestScore = score;
        bestRequest = request;
        requestType = request.type;
      }
    }

    if (bestRequest && bestScore >= (requestType === 'defend_base'
        ? RADIO_CONFIG.BASE_DEFENSE_UTILITY_THRESHOLD
        : this.utilityThreshold)) {

      // For defense requests, acknowledge to claim a slot
      if (requestType === 'defend_base') {
        const acknowledged = this.network.acknowledgeDefenseRequest(
          this.unit.team.id,
          bestRequest.id,
          this.unit.id
        );
        if (!acknowledged) {
          // Slot was taken, try again next update
          return;
        }
      }

      this.responding = true;
      this.currentObjective = {
        requestId: bestRequest.id,
        type: requestType,
        sourceId: bestRequest.sourceId ?? bestRequest.baseId,
        position: { ...bestRequest.position },
        threatLevel: bestRequest.threatLevel,
        // Assist-specific fields
        enemyId: bestRequest.enemyId ?? null,
        enemyLevel: bestRequest.enemyLevel ?? null,
        engageRadius: bestRequest.engageRadius ?? DEFAULT_ENGAGE_RADIUS,
        // Defense-specific fields
        baseId: bestRequest.baseId ?? null,
        patrolRadius: bestRequest.patrolRadius ?? null,
        attackerIds: bestRequest.attackerIds ?? [],
        attackerCount: bestRequest.attackerCount ?? 0,
        // Common fields
        arrivalRadius: bestRequest.arrivalRadius ?? DEFAULT_ARRIVAL_RADIUS,
        issuedAt: bestRequest.timestamp,
        expiresAt: bestRequest.expiresAt
      };

      this.#notifyAssistObjective(this.currentObjective, 'assigned');
    }
  }

  /**
   * Request assistance from teammates
   *
   * Broadcasts a help request to the team radio network.
   * Enforces cooldown to prevent spam (min 2 seconds between broadcasts).
   *
   * Current Usage:
   * - Called by SoldierAI when retreating from combat (SoldierAI.js:2473)
   * - Triggered when losing fight (low health vs stronger enemy)
   *
   * Potential Future Uses:
   * - Base under attack (base calls for defenders)
   * - Found high-value target (call for coordinated attack)
   * - Discovered resources (call for collectors)
   * - Scouting intel (broadcast enemy positions)
   *
   * @param {Object} payload - Request details
   * @param {Object} payload.position - Location to assist at (defaults to unit position)
   * @param {number} payload.threatLevel - Threat level 0-1 (0=low, 1=critical)
   * @param {string} payload.reason - Reason code ('combat_support', 'base_defense', etc.)
   * @param {Object} payload.enemy - Enemy object with id and level
   * @param {number} payload.enemyId - Alternative: enemy ID directly
   * @param {number} payload.enemyLevel - Alternative: enemy level directly
   * @param {number} payload.arrivalRadius - How close responders should get
   * @param {number} payload.engageRadius - Search radius for enemies
   * @param {number} payload.ttl - Request lifetime in milliseconds
   * @returns {Object|null} The broadcast request object, or null if on cooldown
   */
  requestAssist(payload = {}) {
    if (!this.unit || !this.unit.team) return null;

    const now = Date.now();
    // Enforce cooldown to prevent spam
    if (now - this.lastBroadcastTime < this.minBroadcastInterval) {
      return this.activeRequest;
    }

    const request = this.network.broadcastAssistRequest(this.unit.team.id, {
      sourceId: this.unit.id,
      sourceLevel: this.unit.level ?? 1,
      position: payload.position ?? { x: this.unit.x, y: this.unit.y },
      threatLevel: clamp(payload.threatLevel ?? 0.7, 0, 1),
      reason: payload.reason ?? 'combat_support',
      enemyId: payload.enemy?.id ?? payload.enemyId ?? null,
      enemyLevel: payload.enemy?.level ?? payload.enemyLevel ?? null,
      arrivalRadius: payload.arrivalRadius ?? DEFAULT_ARRIVAL_RADIUS,
      engageRadius: payload.engageRadius ?? DEFAULT_ENGAGE_RADIUS,
      ttl: payload.ttl ?? this.requestTtl
    });

    this.activeRequest = request;
    this.lastBroadcastTime = now;

    debugLog(this.unit.level, {
      action: 'radio_broadcast_assist',
      unitId: this.unit.id,
      team: this.unit.team.name,
      requestId: request?.id,
      reason: payload.reason ?? 'combat_support',
      enemyId: payload.enemy?.id ?? null
    });

    return request;
  }

  /**
   * Cancel our outgoing assist request
   *
   * Called when the situation is resolved (enemy defeated, retreated successfully, etc.)
   *
   * @param {string} reason - Reason for cancellation
   */
  resolveOutgoingRequest(reason = 'resolved') {
    if (!this.activeRequest || !this.unit || !this.unit.team) return;
    this.network.cancelRequest(this.unit.team.id, this.activeRequest.id, reason);
    this.activeRequest = null;
  }

  /**
   * Complete assist objective (arrived, enemy defeated, or cancelled)
   *
   * Clears responding state and current objective.
   * Optionally notifies AI to transition out of 'assisting' state.
   *
   * @param {string} reason - Completion reason ('completed', 'expired', 'assist_area_clear')
   * @param {Object} options
   * @param {boolean} options.notifyAI - Whether to notify AI (triggers state transition)
   */
  completeAssistObjective(reason = 'completed', { notifyAI = false } = {}) {
    if (!this.responding) return;

    this.responding = false;
    this.currentObjective = null;

    if (notifyAI) {
      this.#notifyAssistObjective(null, reason);
    }
  }

  /**
   * Notify AI of assist objective changes
   * @private
   */
  #notifyAssistObjective(objective, reason) {
    if (this.ai && typeof this.ai.handleAssistObjectiveUpdate === 'function') {
      this.ai.handleAssistObjectiveUpdate(objective, reason);
    }
  }

  /**
   * Evaluate a request (assist or defense) using utility-based AI scoring
   *
   * Assist Request Scoring:
   * score = (distance * 0.4) + (health * 0.2) + (ammo * 0.2) + (threat * 0.2) - busyPenalty
   *
   * Defense Request Scoring:
   * score = (distance * 0.3) + (health * 0.25) + (ammo * 0.25) + (threat * 0.15) + (loyalty * 0.05) - busyPenalty
   *
   * Defense requests prioritize capability (health/ammo) and add loyalty bonus for defending own base.
   *
   * Factors:
   * - Distance: Closer soldiers score higher
   * - Health: Healthier soldiers more willing to help
   * - Ammo: Well-supplied soldiers more willing
   * - Threat: Higher threat = more urgent response
   * - Loyalty (defense only): Bonus if defending own base
   * - Busy Penalty: Current activity reduces willingness
   *
   * Returns 0 if:
   * - Request is from self (assist only)
   * - Distance exceeds max range
   * - Busy penalty >= 1.0
   *
   * @private
   * @param {Object} request - The request to evaluate
   * @returns {number} Utility score (0-1+, higher = more willing to respond)
   */
  #evaluateRequest(request) {
    if (!this.unit) return 0;

    const busyPenalty = this.#getBusyPenalty(request.type);
    if (busyPenalty >= 1) {
      return 0;
    }

    const position = request.position ?? { x: this.unit.x, y: this.unit.y };
    const distance = this.unit.getDistance(position);

    // Different max distances for different request types
    const maxDistance = request.type === 'defend_base'
      ? RADIO_CONFIG.BASE_DEFENSE_MAX_DISTANCE
      : this.maxAssistDistance;

    const distanceScore = clamp(1 - distance / maxDistance, 0, 1);

    if (distanceScore <= 0) {
      return 0;
    }

    const healthScore = clamp(this.unit.health / this.unit.maxHealth, 0, 1);
    const ammoScore = this.unit.maxAmmo ? clamp(this.unit.ammo / this.unit.maxAmmo, 0, 1) : 1;
    const threatScore = clamp(request.threatLevel ?? 0.5, 0, 1);

    let willingness;
    if (request.type === 'defend_base') {
      // Defense: prioritize capability and add loyalty bonus
      const loyaltyBonus = (this.unit.base && this.unit.base.id === request.baseId) ? 0.05 : 0;
      willingness = (distanceScore * 0.3) + (healthScore * 0.25) + (ammoScore * 0.25) +
                    (threatScore * 0.15) + loyaltyBonus;
    } else {
      // Assist: balanced scoring
      willingness = (distanceScore * 0.4) + (healthScore * 0.2) + (ammoScore * 0.2) + (threatScore * 0.2);
    }

    const finalScore = Math.max(0, willingness - busyPenalty);

    debugLog(this.unit.level, {
      action: 'radio_request_evaluated',
      unitId: this.unit.id,
      team: this.unit.team.name,
      requestType: request.type,
      requestId: request.id,
      score: Number(finalScore.toFixed(3)),
      distance: Number(distance.toFixed(1)),
      unitState: this.unit.state
    });

    return finalScore;
  }

  /**
   * Calculate busy penalty based on unit's current state and condition
   *
   * Higher penalties make unit less willing to respond to requests.
   * Penalty of 1.0 or higher completely prevents response.
   *
   * Defense requests have reduced penalties (soldiers prioritize base defense).
   *
   * Penalty Values (Assist / Defense):
   * - 0.0 / 0.0: Idle/hunting (fully available)
   * - 0.4 / 0.2: Gathering resources (easily interrupted, especially for defense)
   * - 0.5 / 0.3: Fighting/retreating (drop combat for base defense)
   * - 0.6 / 0.4: Returning resource (committed but will help base)
   * - 0.7 / 0.5: Resupplying/reloading (less busy, will help defend)
   * - 0.8 / 0.6: Low health/ammo (still capable of base defense)
   *
   * This creates emergent behavior:
   * - Base defense interrupts almost any task
   * - Soldiers rally to defend their base aggressively
   * - Even wounded soldiers will defend if needed
   *
   * @private
   * @param {string} requestType - 'assist' or 'defend_base'
   * @returns {number} Busy penalty (0-1, higher = less available)
   */
  #getBusyPenalty(requestType = 'assist') {
    if (!this.unit || !this.ai) return 0;

    const state = this.unit.state;
    if (!state) return 0;

    const isDefense = requestType === 'defend_base';
    const defenseMult = isDefense ? 0.6 : 1.0; // 40% reduction for defense

    // In combat - can still respond if urgent
    if (state === 'fighting' || state === 'retreating') {
      return 0.5 * defenseMult;
    }

    // Committed to resource delivery
    if (state === 'returning_resource' && this.unit.carryingResource) {
      return 0.6 * defenseMult;
    }

    // At base resupplying - very busy
    if (state === 'withdrawing' || state === 'reloading' || state === 'resupplying') {
      return 0.7 * defenseMult;
    }

    // Gathering - low commitment, can interrupt
    if (state === 'collecting_corpse' || state === 'gathering' || state === 'gathering_nebula') {
      return 0.4 * defenseMult;
    }

    // Low health/ammo - can't help effectively (but will still defend base)
    if (this.unit.health < this.unit.maxHealth * THRESHOLD_CONFIG.RADIO_MIN_HEALTH_RATIO ||
        this.unit.ammo < this.unit.maxAmmo * THRESHOLD_CONFIG.RADIO_MIN_AMMO_RATIO) {
      return 0.8 * defenseMult;
    }

    // Default: available to respond
    return 0;
  }
}
